import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, LogOut } from "lucide-react";

// Home pubblica: relativa per default (risolve al dominio corrente, es. https://petrolgiuly.it/),
// sovrascrivibile con REACT_APP_PUBLIC_SITE_URL. Nessun URL di preview hardcoded.
const PUBLIC_SITE_URL = process.env.REACT_APP_PUBLIC_SITE_URL || "/";

export default function ChangePassword() {
  const { user, checkAuth, logout } = useAuth();
  const navigate = useNavigate();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user === false) return <Navigate to="/login" replace />;

  const exitToHome = async () => {
    await logout();
    window.location.href = PUBLIC_SITE_URL;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPw.length < 6) return setError("La nuova password deve avere almeno 6 caratteri.");
    if (newPw !== confirm) return setError("Le password non coincidono.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password aggiornata correttamente");
      await checkAuth();
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background enilive-grid-bg px-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center gap-3">
          <a
            href={PUBLIC_SITE_URL}
            aria-label="Torna alla home di Petrol Giuly"
            data-testid="change-pw-logo-link"
            className="cursor-pointer rounded-2xl outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <img src="/logo_icon.png" alt="Petrol Giuly" className="h-20 w-20 rounded-2xl" />
          </a>
          <h1 className="font-display text-2xl font-black">Cambia Password</h1>
          <p className="text-center text-sm text-muted-foreground">
            Per motivi di sicurezza imposta una nuova password personale prima di continuare.
          </p>
        </div>
        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-6 shadow-xl sm:p-8" data-testid="change-pw-form">
          {error && (
            <div className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="change-pw-error">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password attuale</Label>
              <Input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} className="h-12" required data-testid="old-pw-input" />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Nuova password</Label>
              <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="h-12" required data-testid="new-pw-input" />
            </div>
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Conferma nuova password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12" required data-testid="confirm-pw-input" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="mt-6 h-12 w-full text-base font-bold" data-testid="change-pw-submit-btn">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Salva e continua"}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={loading}
            onClick={exitToHome}
            className="mt-3 h-12 w-full gap-2 text-base font-semibold"
            data-testid="change-pw-exit-btn"
          >
            <LogOut className="h-4 w-4" /> Esci e torna alla home
          </Button>
        </form>
      </div>
    </div>
  );
}
